import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isuGroupApi } from '../../../core/api/rtk-query/isu-group.api';
import { userApi } from '../../../core/api/rtk-query/user.api';

const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: grid; place-items: center; z-index: 500;
`;
const Modal = styled.div`
  width: 900px; max-height: 82vh; overflow: hidden; background: #fff; border-radius: 16px; padding: 16px; box-shadow: 0 14px 32px rgba(0,0,0,0.18);
  display: grid; grid-template-rows: auto 1fr auto; gap: 12px;
`;
const Heading = styled.div`display:flex; align-items:center; gap:8px;`;
const Title = styled.h3`margin:0; color:#0b1221;`;
const Sub = styled.div`color:#667;`;
const TableWrap = styled.div`border:1px solid #e5e8f0; border-radius:12px; overflow:auto;`;
const Table = styled.table`
  width:100%; border-collapse:separate; border-spacing:0; color:#0b1221;
  thead th{position:sticky; top:0; background:#f7f9ff; color:#465676; padding:10px; text-align:left; border-bottom:1px solid #e5e8f0}
  tbody td{border-bottom:1px solid #eef1f6; padding:10px}
`;
const Btn = styled.button`background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 12px; border-radius:10px; cursor:pointer;`;
const DangerBtn = styled(Btn)`border-color:#f5c2c7; color:#9b1c1c; background:#fde8ea;`;
const Footer = styled.div`display:flex; justify-content:space-between; align-items:center; gap:8px;`;
const InlineForm = styled.div`display:flex; gap:8px; align-items:center;`;
const Input = styled.input`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221;`;
const CheckboxRow = styled.label`display:flex; align-items:center; gap:8px; color:#465676;`;

type Props = { name: string; onClose: () => void; onChanged?: () => void };

export const IsuGroupDetailsModal = ({ name, onClose, onChanged }: Props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const { data, refetch } = isuGroupApi.useGetUsersByIsuGroupQuery({ name, pageNumber, pageSize });
  const [updateUser] = userApi.useUpdateUserMutation();
  const [rename] = isuGroupApi.useRenameIsuGroupMutation();
  const [deleteIsuGroup] = isuGroupApi.useDeleteIsuGroupMutation();

  useEffect(()=>{ setPageNumber(1); }, [name]);

  const users = data?.users || [];
  const total = data?.number || 0;

  // UI states
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTo, setRenameTo] = useState(name);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeUsers, setRemoveUsers] = useState(false);

  const pages = useMemo(()=> Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const doExclude = async (userId: string) => {
    await updateUser({ id: userId, isuGroup: '' }).unwrap();
    await refetch();
    onChanged?.();
  };

  const doRename = async () => {
    const to = renameTo.trim(); if(!to || to === name) { setRenameOpen(false); return; }
    await rename({ from: name, to }).unwrap();
    setRenameOpen(false);
    await refetch();
    onChanged?.();
  };

  const doDelete = async () => {
    await deleteIsuGroup({ name, removeUsers }).unwrap();
    setDeleteOpen(false);
    onChanged?.();
    onClose();
  };

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={e=> e.stopPropagation()}>
        <Heading>
          <Title>Группа ИСУ: {name || '—'}</Title>
          <Sub>Всего участников: {total}</Sub>
        </Heading>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>ИСУ</th>
                <th>ФИО</th>
                <th>Email</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.isuNumber}</td>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td><Btn onClick={()=> doExclude(u.id)}>Исключить</Btn></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>

        <Footer>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {!renameOpen ? (
              <Btn onClick={()=> { setRenameTo(name); setRenameOpen(true); setDeleteOpen(false); }}>Переименовать группу</Btn>
            ) : (
              <InlineForm>
                <Input placeholder="Новое имя…" value={renameTo} onChange={e=> setRenameTo(e.target.value)} />
                <Btn onClick={doRename}>Сохранить</Btn>
                <Btn onClick={()=> setRenameOpen(false)}>Отмена</Btn>
              </InlineForm>
            )}
            {!deleteOpen ? (
              <DangerBtn onClick={()=> { setDeleteOpen(true); setRenameOpen(false); }}>Удалить группу…</DangerBtn>
            ) : (
              <InlineForm>
                <CheckboxRow>
                  <input type="checkbox" checked={removeUsers} onChange={e=> setRemoveUsers(e.target.checked)} />
                  Удалить также всех студентов этой группы
                </CheckboxRow>
                <DangerBtn onClick={doDelete}>Удалить</DangerBtn>
                <Btn onClick={()=> setDeleteOpen(false)}>Отмена</Btn>
              </InlineForm>
            )}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {Array.from({ length: pages }).map((_, i) => {
              const p = i+1; return (
                <Btn key={p} onClick={()=> setPageNumber(p)} disabled={p===pageNumber}>{p}</Btn>
              );
            })}
            <Btn onClick={onClose}>Закрыть</Btn>
          </div>
        </Footer>
      </Modal>
    </Backdrop>
  );
};


